import { useState, useRef } from 'react';
import { useStore } from '../../store';
import {
  Upload,
  File,
  X,
  HardDrive,
  Loader2,
  Plus,
  FolderOpen,
  Eye,
  FileText,
  Globe,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cloudApi } from '../../services/api';
import { LocalFile, CloudFile } from '../../types';

export default function DocumentPanel() {
  const {
    selectedDocuments,
    addDocumentToSelection,
    removeDocumentFromSelection,
    localFiles,
    setLocalFiles,
    sections,
    updateSection,
    cloudConnections,
    setCloudConnections,
    addNotification,
  } = useStore();

  const [connectingCloud, setConnectingCloud] = useState<string | null>(null);
  const [browsingService, setBrowsingService] = useState<string | null>(null);
  const [cloudFiles, setCloudFiles] = useState<CloudFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [previewFile, setPreviewFile] = useState<LocalFile | CloudFile | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['globalContext', 'localFiles', 'cloudServices'])
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleSection = (id: string) => {
    const next = new Set(expandedSections);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedSections(next);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const localFile: LocalFile = {
          id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          filename: file.name,
          size: file.size,
          content: event.target?.result as string,
        };
        // Add to local files pool (not to global context yet)
        const current = useStore.getState().localFiles || [];
        setLocalFiles([...current, localFile]);
        addNotification(`Uploaded: ${file.name}`, 'success');
      };
      reader.readAsText(file);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddToGlobalContext = (file: LocalFile | CloudFile) => {
    const alreadyInContext = selectedDocuments.some((d) => d.id === file.id);
    if (!alreadyInContext) {
      addDocumentToSelection(file);
      addNotification('Added to global context', 'success');
    }
  };

  const handleRemoveFromGlobalContext = (fileId: string) => {
    removeDocumentFromSelection(fileId);
    addNotification('Removed from global context', 'info');
  };

  const handleDeleteLocalFile = (fileId: string) => {
    const current = useStore.getState().localFiles || [];
    setLocalFiles(current.filter((f) => f.id !== fileId));
    // Also remove from global context if present
    removeDocumentFromSelection(fileId);
    // Also remove from any section-specific documents to prevent stale references
    sections.forEach((section) => {
      if (section.documents?.some((d) => d.id === fileId)) {
        updateSection(section.id, {
          documents: section.documents.filter((d) => d.id !== fileId),
        });
      }
    });
  };

  const handleConnectCloud = async (provider: 'google' | 'dropbox' | 'onedrive') => {
    setConnectingCloud(provider);
    try {
      let url: string;
      if (provider === 'google') {
        url = await cloudApi.getGoogleAuthUrl();
      } else if (provider === 'dropbox') {
        url = await cloudApi.getDropboxAuthUrl();
      } else {
        url = await cloudApi.getOneDriveAuthUrl();
      }
      window.location.href = url;
    } catch {
      addNotification('Failed to initiate cloud connection', 'error');
      setConnectingCloud(null);
    }
  };

  const handleDisconnectCloud = async (provider: 'google' | 'dropbox' | 'onedrive') => {
    try {
      if (provider === 'google') {
        await cloudApi.disconnectGoogle();
        setCloudConnections({ googleDrive: false });
      } else if (provider === 'dropbox') {
        await cloudApi.disconnectDropbox();
        setCloudConnections({ dropbox: false });
      } else {
        await cloudApi.disconnectOneDrive();
        setCloudConnections({ oneDrive: false });
      }
      addNotification('Cloud service disconnected', 'success');
    } catch {
      addNotification('Failed to disconnect cloud service', 'error');
    }
  };

  const handleBrowseCloud = async (provider: 'google' | 'dropbox' | 'onedrive') => {
    setBrowsingService(provider);
    setLoadingFiles(true);
    try {
      let files: CloudFile[];
      if (provider === 'google') {
        files = await cloudApi.getGoogleFiles();
      } else if (provider === 'dropbox') {
        files = await cloudApi.getDropboxFiles();
      } else {
        files = await cloudApi.getOneDriveFiles();
      }
      setCloudFiles(files);
    } catch {
      addNotification('Failed to fetch files', 'error');
      setBrowsingService(null);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleSelectCloudFile = (file: CloudFile) => {
    const docFile: CloudFile = {
      ...file,
      source: browsingService === 'google'
        ? 'googleDrive'
        : browsingService === 'dropbox'
        ? 'dropbox'
        : 'oneDrive',
    };
    // Add to local files pool
    const current = useStore.getState().localFiles || [];
    if (!current.some((f) => f.id === docFile.id)) {
      setLocalFiles([...current, docFile]);
    }
    addNotification(`Imported: ${file.name}`, 'success');
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext || '')) return <FileText size={14} className="text-red-500" />;
    if (['doc', 'docx'].includes(ext || '')) return <FileText size={14} className="text-accent" />;
    if (['txt', 'md'].includes(ext || '')) return <File size={14} className="text-gray-500" />;
    return <File size={14} className="text-gray-400" />;
  };

  const getFilename = (doc: LocalFile | CloudFile) => {
    return 'filename' in doc ? doc.filename : ('name' in doc ? (doc as any).name : 'Document');
  };

  const allLocalFiles = localFiles || [];

  return (
    <div className="space-y-4">
      {/* Global AI Context */}
      <div>
        <button
          onClick={() => toggleSection('globalContext')}
          className="w-full flex items-center justify-between px-3 py-2 bg-accent/10 hover:bg-accent/20 rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            <Globe size={14} className="text-accent" />
            <span className="font-medium text-sm text-accent-dark">
              Global AI Context ({selectedDocuments.length})
            </span>
          </div>
          {expandedSections.has('globalContext') ? (
            <ChevronUp size={14} className="text-accent" />
          ) : (
            <ChevronDown size={14} className="text-accent" />
          )}
        </button>

        {expandedSections.has('globalContext') && (
          <div className="mt-2 space-y-1.5 px-1">
            {selectedDocuments.length === 0 ? (
              <p className="text-xs text-gray-400 px-2 py-2 text-center italic">
                No documents in global context. Add files below to make them available to all sections.
              </p>
            ) : (
              selectedDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 p-2 bg-accent/10/50 rounded-md group hover:bg-accent/10 transition-colors"
                >
                  {getFileIcon(getFilename(doc))}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-700 truncate">
                      {getFilename(doc)}
                    </p>
                    {doc.size && (
                      <p className="text-xs text-gray-400">{formatFileSize(doc.size)}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setPreviewFile(doc)}
                      className="p-1 text-gray-400 hover:text-accent"
                      title="Preview"
                    >
                      <Eye size={12} />
                    </button>
                    <button
                      onClick={() => handleRemoveFromGlobalContext(doc.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Remove from global context"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Local Files */}
      <div>
        <button
          onClick={() => toggleSection('localFiles')}
          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            <HardDrive size={14} className="text-gray-600" />
            <span className="font-medium text-sm text-gray-800">
              Local Files ({allLocalFiles.length})
            </span>
          </div>
          {expandedSections.has('localFiles') ? (
            <ChevronUp size={14} className="text-gray-400" />
          ) : (
            <ChevronDown size={14} className="text-gray-400" />
          )}
        </button>

        {expandedSections.has('localFiles') && (
          <div className="mt-2 space-y-2 px-1">
            {/* Upload area */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center p-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-accent hover:bg-accent/5 cursor-pointer transition-colors"
            >
              <Upload size={18} className="text-gray-400 mb-1" />
              <p className="text-xs text-gray-500">Click to upload files</p>
              <p className="text-xs text-gray-400">PDF, DOCX, TXT, MD, CSV</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.md,.csv,.json"
              onChange={handleFileUpload}
              className="hidden"
            />

            {/* File list */}
            {allLocalFiles.length > 0 && (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {allLocalFiles.map((file) => {
                  const filename = getFilename(file);
                  const isInGlobalContext = selectedDocuments.some((d) => d.id === file.id);

                  return (
                    <div
                      key={file.id}
                      className="flex items-center gap-2 p-2 bg-gray-50 rounded-md group hover:bg-gray-100 transition-colors"
                    >
                      {getFileIcon(filename)}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700 truncate">{filename}</p>
                        <div className="flex items-center gap-1">
                          {file.size && (
                            <span className="text-xs text-gray-400">{formatFileSize(file.size)}</span>
                          )}
                          {isInGlobalContext && (
                            <span className="text-xs text-accent font-medium">- In Context</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setPreviewFile(file)}
                          className="p-1 text-gray-400 hover:text-accent"
                          title="Preview"
                        >
                          <Eye size={12} />
                        </button>
                        {isInGlobalContext ? (
                          <button
                            onClick={() => handleRemoveFromGlobalContext(file.id)}
                            className="p-1 text-accent hover:text-red-600"
                            title="Remove from global context"
                          >
                            <X size={12} />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAddToGlobalContext(file)}
                            className="p-1 text-gray-400 hover:text-accent"
                            title="Add to global context"
                          >
                            <Plus size={12} />
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteLocalFile(file.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          title="Delete file"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cloud Services */}
      <div>
        <button
          onClick={() => toggleSection('cloudServices')}
          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <div className="flex items-center gap-2">
            <FolderOpen size={14} className="text-gray-600" />
            <span className="font-medium text-sm text-gray-800">Cloud Services</span>
          </div>
          {expandedSections.has('cloudServices') ? (
            <ChevronUp size={14} className="text-gray-400" />
          ) : (
            <ChevronDown size={14} className="text-gray-400" />
          )}
        </button>

        {expandedSections.has('cloudServices') && (
          <div className="mt-2 space-y-2 px-1">
            <CloudServiceItem
              name="Google Drive"
              connected={cloudConnections.googleDrive}
              connecting={connectingCloud === 'google'}
              onConnect={() => handleConnectCloud('google')}
              onDisconnect={() => handleDisconnectCloud('google')}
              onBrowse={() => handleBrowseCloud('google')}
            />
            <CloudServiceItem
              name="Dropbox"
              connected={cloudConnections.dropbox}
              connecting={connectingCloud === 'dropbox'}
              onConnect={() => handleConnectCloud('dropbox')}
              onDisconnect={() => handleDisconnectCloud('dropbox')}
              onBrowse={() => handleBrowseCloud('dropbox')}
            />
            <CloudServiceItem
              name="OneDrive"
              connected={cloudConnections.oneDrive}
              connecting={connectingCloud === 'onedrive'}
              onConnect={() => handleConnectCloud('onedrive')}
              onDisconnect={() => handleDisconnectCloud('onedrive')}
              onBrowse={() => handleBrowseCloud('onedrive')}
            />
          </div>
        )}
      </div>

      {/* Cloud File Browser */}
      {browsingService && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-medium text-gray-700 capitalize">
              {browsingService === 'google' ? 'Google Drive' : browsingService === 'dropbox' ? 'Dropbox' : 'OneDrive'} Files
            </span>
            <button
              onClick={() => {
                setBrowsingService(null);
                setCloudFiles([]);
              }}
              className="p-0.5 text-gray-400 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {loadingFiles ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 size={18} className="animate-spin text-accent" />
              </div>
            ) : cloudFiles.length === 0 ? (
              <div className="py-6 text-center text-xs text-gray-400">No files found</div>
            ) : (
              <div className="divide-y divide-gray-100">
                {cloudFiles.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      if (!file.isFolder) handleSelectCloudFile(file);
                    }}
                  >
                    {file.isFolder ? (
                      <FolderOpen size={14} className="text-yellow-500" />
                    ) : (
                      getFileIcon(file.name)
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-700 truncate">{file.name}</p>
                      {file.size && (
                        <p className="text-xs text-gray-400">{formatFileSize(file.size)}</p>
                      )}
                    </div>
                    {!file.isFolder && <Plus size={12} className="text-gray-400" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-800">
                {getFilename(previewFile)}
              </h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {'content' in previewFile && previewFile.content ? (
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono bg-gray-50 p-3 rounded">
                  {previewFile.content.substring(0, 5000)}
                  {(previewFile.content?.length || 0) > 5000 && '\n\n... (truncated)'}
                </pre>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FileText size={32} className="mx-auto mb-2" />
                  <p className="text-sm">Preview not available for cloud files</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CloudServiceItem({
  name,
  connected,
  connecting,
  onConnect,
  onDisconnect,
  onBrowse,
}: {
  name: string;
  connected: boolean;
  connecting: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  onBrowse: () => void;
}) {
  return (
    <div
      className={`flex items-center justify-between p-2.5 rounded-lg border transition-colors ${
        connected ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-2">
        <HardDrive
          size={14}
          className={connected ? 'text-green-600' : 'text-gray-400'}
        />
        <span className="text-xs font-medium text-gray-700">{name}</span>
      </div>
      <div className="flex items-center gap-2">
        {connecting ? (
          <Loader2 size={14} className="animate-spin text-accent" />
        ) : connected ? (
          <>
            <button
              onClick={onBrowse}
              className="text-xs text-accent hover:text-accent-dark hover:underline"
            >
              Browse
            </button>
            <button
              onClick={onDisconnect}
              className="text-xs text-red-500 hover:text-red-600 hover:underline"
            >
              Disconnect
            </button>
          </>
        ) : (
          <button
            onClick={onConnect}
            className="flex items-center gap-1 text-xs text-accent hover:text-accent-dark"
          >
            <Plus size={10} />
            Connect
          </button>
        )}
      </div>
    </div>
  );
}
